import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  PencilSimple, FloppyDisk, ArrowCounterClockwise, ArrowClockwise,
  X, FileArrowDown,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEXT_EXTS = new Set(['txt']);
const IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png', 'webp', 'bmp', 'gif']);

function extOf(filename) {
  const i = filename.lastIndexOf('.');
  return i >= 0 ? filename.slice(i + 1).toLowerCase() : '';
}

/**
 * Post-conversion "Edit & re-download" panel.
 * Supports text (.txt) and image outputs.
 * Renders nothing (returns null) for outputs it can't edit (e.g. .docx, .pdf, .zip).
 */
export default function OutputEditor({ result, onSaved }) {
  const ext = extOf(result.filename);
  const [mode, setMode] = useState('idle'); // idle | text | image
  const [textContent, setTextContent] = useState('');
  const [saving, setSaving] = useState(false);

  // Image edit state
  const [rotate, setRotate] = useState(0);
  const [cropLeft, setCropLeft] = useState(0);
  const [cropTop, setCropTop] = useState(0);
  const [cropRight, setCropRight] = useState(0);
  const [cropBottom, setCropBottom] = useState(0);

  const isText = TEXT_EXTS.has(ext);
  const isImage = IMAGE_EXTS.has(ext);
  const editable = isText || isImage;

  useEffect(() => {
    if (mode === 'text' && !textContent) {
      (async () => {
        try {
          const r = await axios.get(`${API}/preview-text/${result.job_id}`);
          setTextContent(r.data.content || '');
        } catch (e) {
          toast.error('Failed to load text');
          setMode('idle');
        }
      })();
    }
  }, [mode, result.job_id, textContent]);

  if (!editable) {
    return (
      <div data-testid="editor-unavailable" className="mt-6 border-2 border-black bg-zinc-50 p-4 text-sm text-zinc-700">
        <strong>Editing this file type in-browser</strong> (.{ext}) requires a full Office editor. For now,
        download the file, edit it in Word / Excel / a PDF app, and re-upload if you need further conversion.
      </div>
    );
  }

  async function saveText(target) {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('content', textContent);
      fd.append('target', target);
      fd.append('source_job', result.job_id);
      const r = await axios.post(`${API}/edit/text`, fd);
      toast.success('Saved!');
      onSaved && onSaved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function saveImage() {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('source_job', result.job_id);
      fd.append('rotate', String(rotate));
      fd.append('crop_left', String(cropLeft));
      fd.append('crop_top', String(cropTop));
      fd.append('crop_right', String(cropRight));
      fd.append('crop_bottom', String(cropBottom));
      const r = await axios.post(`${API}/edit/image`, fd);
      toast.success('Saved!');
      onSaved && onSaved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (mode === 'idle') {
    return (
      <div className="mt-6">
        <button
          data-testid="open-editor-btn"
          onClick={() => setMode(isText ? 'text' : 'image')}
          className="brutal-btn secondary inline-flex items-center gap-2"
        >
          <PencilSimple weight="bold" size={16} />
          Edit before downloading
        </button>
      </div>
    );
  }

  return (
    <div data-testid="editor-panel" className="mt-6 border-2 border-black bg-white p-5 brutal-shadow text-left">
      <div className="flex items-center justify-between mb-4">
        <p className="font-display font-semibold text-lg">Edit &amp; save</p>
        <button
          data-testid="close-editor-btn"
          onClick={() => setMode('idle')}
          className="w-8 h-8 grid place-items-center border-2 border-black bg-white hover:bg-black hover:text-white"
          style={{ transition: 'background-color 0.15s ease, color 0.15s ease' }}
          aria-label="Close editor"
        >
          <X weight="bold" size={14} />
        </button>
      </div>

      {mode === 'text' && (
        <>
          <textarea
            data-testid="editor-textarea"
            value={textContent}
            onChange={(e) => setTextContent(e.target.value)}
            className="w-full h-72 p-3 border-2 border-black font-mono-plex text-sm focus:outline-none"
            placeholder="Edit your text here…"
          />
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              data-testid="save-as-txt-btn"
              onClick={() => saveText('txt')}
              disabled={saving}
              className="brutal-btn inline-flex items-center gap-2 disabled:opacity-40"
            >
              <FloppyDisk weight="bold" size={16} /> Save as .txt
            </button>
            <button
              data-testid="save-as-pdf-btn"
              onClick={() => saveText('pdf')}
              disabled={saving}
              className="brutal-btn secondary inline-flex items-center gap-2 disabled:opacity-40"
            >
              <FloppyDisk weight="bold" size={16} /> Save as .pdf
            </button>
          </div>
        </>
      )}

      {mode === 'image' && (
        <>
          <div className="border-2 border-black bg-zinc-50 grid place-items-center p-3">
            <img
              data-testid="editor-image-preview"
              src={`${BACKEND_URL}/api/preview/${result.job_id}`}
              alt="Preview"
              style={{ transform: `rotate(${rotate}deg)`, maxHeight: 320, maxWidth: '100%' }}
            />
          </div>

          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ['Crop left (px)', cropLeft, setCropLeft, 'crop-left-input'],
              ['Crop top (px)', cropTop, setCropTop, 'crop-top-input'],
              ['Crop right (px)', cropRight, setCropRight, 'crop-right-input'],
              ['Crop bottom (px)', cropBottom, setCropBottom, 'crop-bottom-input'],
            ].map(([label, val, setter, testid]) => (
              <label key={label} className="block">
                <span className="block text-xs font-mono-plex uppercase tracking-widest text-zinc-500 mb-1">{label}</span>
                <input
                  data-testid={testid}
                  type="number"
                  min={0}
                  value={val}
                  onChange={(e) => setter(Math.max(0, parseInt(e.target.value || '0', 10)))}
                  className="w-full px-3 py-2 border-2 border-black bg-white font-mono-plex text-sm focus:outline-none"
                />
              </label>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap gap-3">
            <button
              data-testid="rotate-left-btn"
              onClick={() => setRotate((r) => (r + 270) % 360)}
              className="brutal-btn secondary inline-flex items-center gap-2"
              type="button"
            >
              <ArrowCounterClockwise weight="bold" size={16} /> Rotate left
            </button>
            <button
              data-testid="rotate-right-btn"
              onClick={() => setRotate((r) => (r + 90) % 360)}
              className="brutal-btn secondary inline-flex items-center gap-2"
              type="button"
            >
              <ArrowClockwise weight="bold" size={16} /> Rotate right
            </button>
            <button
              data-testid="save-image-btn"
              onClick={saveImage}
              disabled={saving}
              className="brutal-btn inline-flex items-center gap-2 disabled:opacity-40"
            >
              <FileArrowDown weight="bold" size={16} /> Save edited image
            </button>
          </div>
        </>
      )}
    </div>
  );
}
